import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { salesApi, inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge as SharedStatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Sales Orders — FR-SALES-01
 * Order lifecycle: draft → confirmed → picked → packed → shipped → invoiced → paid
 *
 * Two-pane layout on large screens: list on the left, detail on the right.
 * On smaller screens the detail drops below the list.
 */
export function OrdersPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['sales', 'orders', statusFilter],
    // Fetch all; status filtering on the client keeps KPI totals accurate across tabs.
    queryFn: () => salesApi.listOrders({ limit: 500 }),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (listQuery.data ?? []) as any[];
    const kpis = {
      total: rows.length,
      open: rows.filter((o) => ['draft', 'confirmed', 'picked', 'packed'].includes(o.status)).length,
      shipping: rows.filter((o) => ['shipped', 'invoiced'].includes(o.status)).length,
      paid: rows.filter((o) => o.status === 'paid').length,
    };
    const filtered = statusFilter
      ? rows.filter((o) => o.status === statusFilter)
      : rows;
    return { kpis, filteredRows: filtered };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '',         label: 'All' },
    { value: 'draft',    label: 'Draft' },
    { value: 'confirmed',label: 'Confirmed' },
    { value: 'picked',   label: 'Picked' },
    { value: 'packed',   label: 'Packed' },
    { value: 'shipped',  label: 'Shipped' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid',     label: 'Paid' },
    { value: 'cancelled',label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Sales orders"
        subtitle="Draft → confirm → pick → pack → ship → invoice. All in one flow."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New order
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total orders" value={kpis.total} />
        <KpiTile label="Open" value={kpis.open} accent="amber" />
        <KpiTile label="Fulfilling" value={kpis.shipping} accent="sage" />
        <KpiTile label="Paid" value={kpis.paid} accent="sage" />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        {/* LEFT: list */}
        <div className="card overflow-hidden">
          {listQuery.isLoading ? (
            <LoadingState rows={6} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message="No orders"
              hint={statusFilter
                ? `No orders in "${statusFilter}" status.`
                : 'Create your first order to see it here.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((o: any) => (
                    <tr
                      key={o.id}
                      onClick={() => setSelected(o.id)}
                      className={clsx(
                        'cursor-pointer',
                        selected === o.id && '!bg-canvas-subtle'
                      )}
                    >
                      <td className="font-mono text-xs text-ink-900 font-semibold">
                        {o.order_number}
                      </td>
                      <td className="text-sm text-ink-700 truncate max-w-[12rem]">
                        {o.customer_name}
                      </td>
                      <td className="text-sm text-ink-500">
                        {new Date(o.order_date).toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </td>
                      <td><SharedStatusBadge status={o.status} /></td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        ${parseFloat(o.grand_total).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* RIGHT: detail */}
        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select an order"
              hint="Click a row on the left to view order details and lifecycle actions."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              }
            />
          ) : (
            <OrderDetail
              orderId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['sales'] })}
            />
          )}
        </div>
      </div>

      {showCreate && <CreateOrderModal
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['sales', 'orders'] });
          setShowCreate(false);
          setSelected(id);
        }}
      />}
    </div>
  );
}

// ============================================================
// OrderDetail — the workflow panel
// ============================================================
function OrderDetail({ orderId, onChanged }: { orderId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sales', 'order', orderId], queryFn: () => salesApi.getOrder(orderId) });

  const [showConfirm, setShowConfirm] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showShip, setShowShip] = useState(false);

  const confirmMut = useMutation({
    mutationFn: () => salesApi.confirmOrder(orderId),
    onSuccess: () => {
      toast.success('Confirmed — stock reserved');
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] });
      onChanged();
      setShowConfirm(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: (reason: string) => salesApi.cancelOrder(orderId, reason),
    onSuccess: () => {
      toast.success('Cancelled');
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] });
      onChanged();
      setShowCancel(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pickMut = useMutation({
    mutationFn: () => {
      const picks = (q.data?.lines ?? []).map((l: any) => ({
        lineId: l.id, pickedQty: parseFloat(l.quantity),
      }));
      return salesApi.pickOrder(orderId, picks);
    },
    onSuccess: () => { toast.success('Picked'); qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const packMut = useMutation({
    mutationFn: () => {
      const packs = (q.data?.lines ?? []).map((l: any) => ({
        lineId: l.id, packedQty: parseFloat(l.picked_qty),
      }));
      return salesApi.packOrder(orderId, packs);
    },
    onSuccess: () => { toast.success('Packed'); qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const shipMut = useMutation({
    mutationFn: ({ carrier, tracking }: any) => salesApi.shipOrder(orderId, {
      trackingCarrier: carrier, trackingNumber: tracking, shipAll: true,
    }),
    onSuccess: () => {
      toast.success('Shipped — stock movements recorded');
      qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] });
      onChanged();
      setShowShip(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const invoiceMut = useMutation({
    mutationFn: () => salesApi.invoiceOrder(orderId, { autoPost: true }),
    onSuccess: (d) => { toast.success(`Invoice ${d.invoiceNumber} posted. JE: ${d.journalEntryNumber ?? 'n/a'}`, { duration: 6000 }); qc.invalidateQueries({ queryKey: ['sales', 'order', orderId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const o = q.data;

  const canCancel = ['draft','confirmed','picked','packed'].includes(o.status);
  const canInvoice = ['confirmed','picked','packed','shipped'].includes(o.status) && !o.invoice_id;

  return (
    <div>
      {/* Detail header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{o.order_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{o.customer_name}</div>
          <div className="text-xs text-ink-500 mt-1">
            {new Date(o.order_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            {o.required_date && (
              <>
                <span className="mx-1.5 text-ink-300">·</span>
                due {new Date(o.required_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </>
            )}
          </div>
          {o.warehouse_code && (
            <div className="text-xs text-ink-500 mt-0.5">
              📦 {o.warehouse_code} {o.warehouse_name}
            </div>
          )}
          {o.sales_rep_name && (
            <div className="text-xs text-ink-500 mt-0.5">
              Rep: {o.sales_rep_name}
            </div>
          )}
        </div>
        <SharedStatusBadge status={o.status} />
      </div>

      {/* Alerts */}
      {o.cancelled_at && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          <div className="font-semibold">Cancelled</div>
          <div className="mt-0.5">
            {new Date(o.cancelled_at).toLocaleString()} · {o.cancelled_by_name}
          </div>
          {o.cancellation_reason && <div className="mt-1 text-red-600">{o.cancellation_reason}</div>}
        </div>
      )}
      {o.tracking_number && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-3">
          <div className="font-semibold">Shipped</div>
          <div className="mt-0.5">
            {o.tracking_carrier ?? 'Carrier'} · <span className="font-mono">{o.tracking_number}</span>
          </div>
        </div>
      )}
      {o.invoice_number && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-3">
          Invoice: <span className="font-mono font-semibold">{o.invoice_number}</span>
        </div>
      )}

      {/* Line items */}
      <div className="border border-ink-100 rounded-xl overflow-x-auto mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Ord</th>
              <th className="text-right">Pick</th>
              <th className="text-right">Pack</th>
              <th className="text-right">Ship</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {o.lines.map((l: any) => {
              const physical = l.stock_physical ? parseFloat(l.stock_physical) : 0;
              const reserved = l.stock_reserved ? parseFloat(l.stock_reserved) : 0;
              const available = physical - reserved;
              const qty = parseFloat(l.quantity);
              const lowStock = o.status === 'draft' && available < qty;
              return (
                <tr key={l.id}>
                  <td>
                    <div className="font-medium text-ink-900">{l.description}</div>
                    {l.sku && <div className="font-mono text-2xs text-ink-500">{l.sku}</div>}
                    {lowStock && (
                      <div className="text-2xs text-danger font-semibold mt-1">
                        Only {available} available
                      </div>
                    )}
                  </td>
                  <td className="text-right font-mono tabular-nums">{qty.toFixed(0)}</td>
                  <td className="text-right font-mono text-ink-500 tabular-nums">
                    {parseFloat(l.picked_qty).toFixed(0)}
                  </td>
                  <td className="text-right font-mono text-ink-500 tabular-nums">
                    {parseFloat(l.packed_qty).toFixed(0)}
                  </td>
                  <td className="text-right font-mono text-ink-500 tabular-nums">
                    {parseFloat(l.shipped_qty).toFixed(0)}
                  </td>
                  <td className="text-right font-mono font-semibold tabular-nums whitespace-nowrap">
                    ${parseFloat(l.line_total).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-ink-600">
            <span>Subtotal</span>
            <span className="font-mono tabular-nums">{fmtCurrency(parseFloat(o.subtotal))}</span>
          </div>
          {parseFloat(o.discount_total) > 0 && (
            <div className="flex justify-between text-ink-600">
              <span>Discount</span>
              <span className="font-mono tabular-nums">−${parseFloat(o.discount_total).toFixed(2)}</span>
            </div>
          )}
          {parseFloat(o.tax_total) > 0 && (
            <div className="flex justify-between text-ink-600">
              <span>Tax</span>
              <span className="font-mono tabular-nums">{fmtCurrency(parseFloat(o.tax_total))}</span>
            </div>
          )}
          {parseFloat(o.shipping_total) > 0 && (
            <div className="flex justify-between text-ink-600">
              <span>Shipping</span>
              <span className="font-mono tabular-nums">{fmtCurrency(parseFloat(o.shipping_total))}</span>
            </div>
          )}
        </div>
        <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-ink-200">
          <span className="font-semibold text-ink-900">Total</span>
          <span className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${parseFloat(o.grand_total).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {canCancel && (
          <button
            className="btn-secondary"
            disabled={cancelMut.isPending}
            onClick={() => setShowCancel(true)}
          >
            Cancel order
          </button>
        )}
        {o.status === 'draft' && (
          <button
            className="btn-primary"
            disabled={confirmMut.isPending}
            onClick={() => setShowConfirm(true)}
          >
            {confirmMut.isPending ? 'Confirming…' : 'Confirm'}
          </button>
        )}
        {o.status === 'confirmed' && (
          <button className="btn-primary" disabled={pickMut.isPending} onClick={() => pickMut.mutate()}>
            {pickMut.isPending ? 'Picking…' : 'Pick full qty'}
          </button>
        )}
        {o.status === 'picked' && (
          <button className="btn-primary" disabled={packMut.isPending} onClick={() => packMut.mutate()}>
            {packMut.isPending ? 'Packing…' : 'Pack all picked'}
          </button>
        )}
        {o.status === 'packed' && (
          <button
            className="btn-primary"
            disabled={shipMut.isPending}
            onClick={() => setShowShip(true)}
          >
            {shipMut.isPending ? 'Shipping…' : 'Ship'}
          </button>
        )}
        {canInvoice && (
          <button className="btn-primary" disabled={invoiceMut.isPending} onClick={() => invoiceMut.mutate()}>
            {invoiceMut.isPending ? 'Invoicing…' : 'Generate invoice'}
          </button>
        )}
      </div>

      {showConfirm && (
        <ConfirmOrderDialog
          orderNumber={o.order_number}
          loading={confirmMut.isPending}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => confirmMut.mutate()}
        />
      )}
      {showCancel && (
        <CancelOrderDialog
          orderNumber={o.order_number}
          loading={cancelMut.isPending}
          onClose={() => setShowCancel(false)}
          onConfirm={(reason) => cancelMut.mutate(reason)}
        />
      )}
      {showShip && (
        <ShipOrderDialog
          orderNumber={o.order_number}
          loading={shipMut.isPending}
          onClose={() => setShowShip(false)}
          onConfirm={(carrier, tracking) => shipMut.mutate({ carrier, tracking })}
        />
      )}
    </div>
  );
}

// ============================================================
// Order workflow dialogs
// ============================================================
function ConfirmOrderDialog({
  orderNumber, loading, onClose, onConfirm,
}: { orderNumber: string; loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Confirm</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Confirm order?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Order <span className="font-mono font-semibold text-ink-900">{orderNumber}</span> will move to
          <span className="font-semibold"> confirmed</span> and its line quantities will be reserved against
          available stock. You can still cancel or edit after confirming.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading} onClick={onConfirm}>
          {loading ? 'Confirming…' : 'Confirm order'}
        </button>
      </div>
    </Modal>
  );
}

function CancelOrderDialog({
  orderNumber, loading, onClose, onConfirm,
}: { orderNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Commerce · Cancel</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Cancel order?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Cancel <span className="font-mono font-semibold text-ink-900">{orderNumber}</span>?
          Reserved stock will be released. This cannot be undone — a replacement order
          would need to be created from scratch.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer changed their mind"
        />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep it</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Cancelling…' : 'Cancel order'}
        </button>
      </div>
    </Modal>
  );
}

function ShipOrderDialog({
  orderNumber, loading, onClose, onConfirm,
}: {
  orderNumber: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (carrier: string | undefined, tracking: string) => void;
}) {
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Ship</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Ship order</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Ship <span className="font-mono font-semibold text-ink-900">{orderNumber}</span>.
          This records stock movements for every line and moves the order to shipped status.
        </p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Carrier <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input
            className="input"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="UPS · FedEx · DHL · USPS"
          />
        </div>
        <div>
          <label className="label">Tracking number <span className="text-danger">*</span></label>
          <input
            className="input font-mono"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="1Z999AA10123456784"
          />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!tracking.trim() || loading}
          onClick={() => onConfirm(carrier.trim() || undefined, tracking.trim())}
        >
          {loading ? 'Shipping…' : 'Ship order'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// KpiTile
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// ============================================================
// CreateOrderModal — preserved logic, restyled shell
// ============================================================
function CreateOrderModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const in14 = (() => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); })();
  const [customerId, setCustomerId] = useState('');
  const [requiredDate, setRequiredDate] = useState(in14);
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [lines, setLines] = useState<Array<any>>([
    { productId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, taxRate: 0 },
  ]);
  const [notes, setNotes] = useState('');

  const custQuery = useQuery({ queryKey: ['customers'], queryFn: () => salesApi.listCustomers({ limit: 500 }) });
  const prodQuery = useQuery({
    queryKey: ['products-short'],
    queryFn: () => inventoryApi.listProducts({ pageSize: 500, status: 'active' }),
  });
  const products = prodQuery.data?.data ?? [];

  const mut = useMutation({
    mutationFn: () => salesApi.createOrder({
      customerId, requiredDate, paymentTerms,
      notes: notes || undefined,
      lines: lines.filter((l) => l.productId && l.quantity > 0).map((l) => ({
        productId: l.productId,
        description: l.description || undefined,
        quantity: l.quantity, unitPrice: l.unitPrice,
        discountPercent: l.discountPercent,
        taxRate: l.taxRate,
      })),
    }),
    onSuccess: (d) => { toast.success(`Order ${d.orderNumber} drafted`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const handleProductChange = (idx: number, productId: string) => {
    const prod = products.find((p: any) => p.id === productId);
    setLines((prev) => prev.map((l, j) => j === idx ? {
      ...l,
      productId,
      description: prod?.name ?? l.description,
      unitPrice: prod?.base_price ? parseFloat(prod.base_price) : l.unitPrice,
    } : l));
  };

  const grandTotal = lines.reduce((s, l) => {
    const gross = l.quantity * l.unitPrice;
    const disc = gross * (l.discountPercent / 100);
    const net = gross - disc;
    return s + net + net * (l.taxRate / 100);
  }, 0);

  const canSubmit = customerId && grandTotal > 0 && !mut.isPending;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Commerce</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New sales order</h2>
      </div>

      <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Customer</label>
          <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select customer…</option>
            {custQuery.data?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Required date</label>
          <input type="date" className="input" value={requiredDate}
            onChange={(e) => setRequiredDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Payment terms</label>
          <input className="input" value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)} />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto">
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right w-20">Qty</th>
                <th className="text-right w-24">Unit</th>
                <th className="text-right w-16">Disc %</th>
                <th className="text-right w-16">Tax %</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select
                      className="input text-xs"
                      value={l.productId}
                      onChange={(e) => handleProductChange(i, e.target.value)}
                    >
                      <option value="">Select product…</option>
                      {products.map((p: any) => (
                        <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-right">
                    <input type="number" min={0.01} step={1}
                      className="input w-20 text-right tabular-nums" value={l.quantity}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0} step={0.01}
                      className="input w-24 text-right tabular-nums" value={l.unitPrice}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPrice: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0} max={100} step={0.1}
                      className="input w-16 text-right tabular-nums" value={l.discountPercent}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, discountPercent: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0} max={100} step={0.1}
                      className="input w-16 text-right tabular-nums" value={l.taxRate}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, taxRate: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td>
                    <button
                      className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove line"
                    >✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="mt-2 text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors"
          onClick={() => setLines((prev) => [
            ...prev,
            { productId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, taxRate: 0 },
          ])}
        >
          + Add line
        </button>

        <div className="mt-4">
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal note visible on invoice" />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow text-ink-500">Total</div>
          <div className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${grandTotal.toFixed(2)}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!canSubmit} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
